import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ENDPOINTS, resolveAssetUrl, resolveHtmlAssetUrls, canonicalUrl } from '../config';
import NotFound from './NotFound';

function formatDate(value) {
    if (!value) return '';
    try {
        return new Date(value).toLocaleDateString('en-KE', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    } catch {
        return '';
    }
}

/** Strip WP/Jodit inline colors & white boxes so theme CSS can control readability. */
function sanitizeBlogHtml(html) {
    if (!html) return '';
    return html
        .replace(/<style[\s\S]*?<\/style>/gi, '')
        .replace(/<script[\s\S]*?<\/script>/gi, '')
        .replace(/\sbgcolor=("|')[^"']*\1/gi, '')
        .replace(/\sstyle=(")([^"]*)\1/gi, (_, q, styles) => {
            const cleaned = styles
                .split(';')
                .map((s) => s.trim())
                .filter(Boolean)
                .filter(
                    (decl) =>
                        !/^(color|background|background-color|background-image)\s*:/i.test(
                            decl
                        )
                )
                .join('; ');
            return cleaned ? ` style=${q}${cleaned}${q}` : '';
        })
        .replace(/\sstyle=(')([^']*)\1/gi, (_, q, styles) => {
            const cleaned = styles
                .split(';')
                .map((s) => s.trim())
                .filter(Boolean)
                .filter(
                    (decl) =>
                        !/^(color|background|background-color|background-image)\s*:/i.test(
                            decl
                        )
                )
                .join('; ');
            return cleaned ? ` style=${q}${cleaned}${q}` : '';
        });
}

export default function BlogDetail() {
    const { slug } = useParams();
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setBlog(null);
        setLoading(true);
        setNotFound(false);

        const fetchBlog = async () => {
            try {
                const res = await fetch(ENDPOINTS.BLOGS.BY_SLUG(slug));
                const data = await res.json();
                if (data.isOk && data.data) {
                    setBlog(data.data);
                } else {
                    setNotFound(true);
                }
            } catch (err) {
                console.error('Error fetching blog:', err);
                setNotFound(true);
            } finally {
                setLoading(false);
            }
        };

        if (slug) fetchBlog();
    }, [slug]);

    if (loading) {
        return (
            <main className="pt-[10px] pb-16">
                <div className="max-w-[900px] mx-auto px-4 animate-pulse">
                    <div className="h-8 bg-muted rounded w-2/3 mb-4"></div>
                    <div className="h-4 bg-muted rounded w-1/4 mb-8"></div>
                    <div className="h-64 bg-muted rounded mb-8"></div>
                    <div className="space-y-3">
                        {[...Array(8)].map((_, i) => (
                            <div
                                key={i}
                                className={`h-4 bg-muted rounded ${i % 3 === 2 ? 'w-2/3' : 'w-full'}`}
                            ></div>
                        ))}
                    </div>
                </div>
            </main>
        );
    }

    if (notFound || !blog) {
        return <NotFound />;
    }

    const plainDescription = (blog.description || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    const metaTitle = blog.metaTitle?.trim() || `${blog.title} | FoneXpress Kenya`;
    const metaDescription =
        blog.metaDescription?.trim() ||
        blog.excerpt?.trim() ||
        plainDescription.slice(0, 160);
    const metaKeywords = blog.metaKeywords?.trim() || '';
    const imageUrl = resolveAssetUrl(blog.featuredImage);
    const tags = Array.isArray(blog.tags) ? blog.tags : [];
    const safeHtml = resolveHtmlAssetUrls(sanitizeBlogHtml(blog.description || ''));

    return (
        <main className="pt-[10px] pb-16 bg-background text-foreground">
            <Helmet>
                <title>{metaTitle}</title>
                <link rel="canonical" href={canonicalUrl(`/blog/${blog.slug}`)} />
                {metaDescription ? (
                    <meta name="description" content={metaDescription} />
                ) : null}
                {metaKeywords ? (
                    <meta name="keywords" content={metaKeywords} />
                ) : null}
            </Helmet>

            <div className="max-w-[900px] mx-auto px-4">
                <nav className="py-4 text-sm">
                    <ol className="flex items-center gap-2 text-muted-foreground flex-wrap">
                        <li>
                            <Link to="/" className="hover:text-primary transition-colors">
                                Home
                            </Link>
                        </li>
                        <li>
                            <i className="ri-arrow-right-s-line"></i>
                        </li>
                        <li>
                            <Link to="/blog/" className="hover:text-primary transition-colors">
                                Blog
                            </Link>
                        </li>
                        <li>
                            <i className="ri-arrow-right-s-line"></i>
                        </li>
                        <li className="text-foreground font-medium line-clamp-1">
                            {blog.title}
                        </li>
                    </ol>
                </nav>

                <article>
                    <header className="mb-6">
                        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
                            {blog.title}
                        </h1>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                            {(blog.publishDate || blog.createdAt) && (
                                <span>{formatDate(blog.publishDate || blog.createdAt)}</span>
                            )}
                            {blog.category && (
                                <>
                                    <span>·</span>
                                    <span>{blog.category}</span>
                                </>
                            )}
                            {blog.author && (
                                <>
                                    <span>·</span>
                                    <span>By {blog.author}</span>
                                </>
                            )}
                        </div>
                    </header>

                    {imageUrl && (
                        <div className="relative mb-8 rounded-lg overflow-hidden border border-border">
                            <img
                                src={imageUrl}
                                alt={blog.title}
                                className="w-full h-auto"
                            />
                            {tags.length > 0 && (
                                <div className="absolute top-3 left-3 right-3 flex flex-wrap gap-1.5">
                                    {tags.map((tag) => (
                                        <span
                                            key={tag}
                                            className="px-2.5 py-1 rounded bg-black/70 text-white text-xs font-medium backdrop-blur-sm"
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {!imageUrl && tags.length > 0 && (
                        <div className="mb-6 flex flex-wrap gap-1.5">
                            {tags.map((tag) => (
                                <span
                                    key={tag}
                                    className="px-2.5 py-1 rounded bg-muted text-foreground text-xs font-medium"
                                >
                                    {tag}
                                </span>
                            ))}
                        </div>
                    )}

                    <div
                        className="blog-content"
                        dangerouslySetInnerHTML={{ __html: safeHtml }}
                    />
                </article>

                <div className="mt-10 pt-6 border-t border-border">
                    <Link
                        to="/blog/"
                        className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
                    >
                        <i className="ri-arrow-left-line"></i>
                        Back to Blog
                    </Link>
                </div>
            </div>
        </main>
    );
}
